// useMasonryLayout.js
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { debounce } from "lodash";
import {
  getWidgetPosition,
  getWidgetViewportPosition,
} from "@/utils/widgetUtils";

const getInitialCardPosition = (index, containerWidth) => {
  const centerX = containerWidth / 2;
  const offsetX = index * 2;
  const offsetY = index * 2;

  return {
    left: centerX - 150 + offsetX,
    top: window.innerHeight * 0.4 + offsetY,
    width: 300,
    opacity: 1,
    scale: 0.95,
    rotate: (Math.random() * 2 - 1) * Math.min(8, 2 + index * 0.5),
  };
};

export const useMasonryLayout = ({
  items,
  totalColumns,
  gapPixels,
  onLayoutComplete,
  previewMode,
}) => {
  const [positions, setPositions] = useState({});
  const [isInitialLayout, setIsInitialLayout] = useState(true);
  const [measuredHeights, setMeasuredHeights] = useState({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [layoutInitialized, setLayoutInitialized] = useState(false);

  const gridRef = useRef(null);
  const measureRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const mountedRef = useRef(true);

  // Track when items change, especially for published space refreshes
  const itemsSignature = useMemo(() => {
    return items.map((item) => item.id).join(",");
  }, [items]);

  const calculateWidgetDimensions = useCallback(
    (item) => {
      // Add safeguards to prevent NaN values
      if (
        !containerWidth ||
        containerWidth <= 0 ||
        !totalColumns ||
        totalColumns <= 0
      ) {
        return { width: 200, height: 200 }; // Return fallback dimensions
      }

      // Calculate the width of a single column (accounting for gaps)
      const totalGapWidth = gapPixels * (totalColumns - 1);
      const usableWidth = containerWidth - totalGapWidth;

      // Ensure usableWidth is positive
      if (usableWidth <= 0) {
        return { width: 200, height: 200 }; // Return fallback dimensions
      }

      const columnWidth = usableWidth / totalColumns;

      // Ensure columnWidth is positive
      if (columnWidth <= 0) {
        return { width: 200, height: 200 }; // Return fallback dimensions
      }

      // Get the number of columns and rows for this widget
      const cols = Math.min(
        item.layout[previewMode]?.columns || 4,
        totalColumns
      );
      const rows = item.layout[previewMode]?.columnHeight || cols;

      // Calculate width including gaps for the widget's columns
      const widgetGapWidth = gapPixels * (cols - 1);
      const width = columnWidth * cols + widgetGapWidth;

      // Calculate height based on column units
      // Each column unit is a square, so we use columnWidth as the base unit
      const baseColumnHeight = columnWidth; // Each column unit is square
      const totalGapHeight = gapPixels * (rows - 1);
      const height = baseColumnHeight * rows + totalGapHeight;

      // Ensure final dimensions are valid numbers
      return {
        width: isNaN(width) || width <= 0 ? 200 : width,
        height: isNaN(height) || height <= 0 ? 200 : height,
      };
    },
    [containerWidth, totalColumns, gapPixels, previewMode]
  );

  const syncMeasureContainerWidth = useCallback(() => {
    if (!gridRef.current || isResizing) return;
    const width = gridRef.current.offsetWidth;
    if (width !== containerWidth) {
      setContainerWidth(width);
    }
  }, [containerWidth, isResizing]);

  const measureItems = useCallback(() => {
    if (!measureRef.current) return;
    const newHeights = {};

    const children = measureRef.current.children;
    for (let i = 0; i < children.length; i++) {
      const item = items[i];
      if (!item) continue;
      const child = children[i];
      const height = child.getBoundingClientRect().height;
      if (height > 0) {
        newHeights[item.id] = height;
      }
    }

    if (Object.keys(newHeights).length > 0) {
      setMeasuredHeights((prev) => {
        let changed = false;
        for (let key in newHeights) {
          if (prev[key] !== newHeights[key]) {
            changed = true;
            break;
          }
        }
        return changed ? { ...prev, ...newHeights } : prev;
      });
    }
  }, [items]);

  const performMasonryLayout = useCallback(
    (width, layoutItems = items) => {
      if (!Object.keys(measuredHeights).length) return;
      if (!mountedRef.current) return;

      // Add safeguards to prevent NaN values
      if (!width || width <= 0 || !totalColumns || totalColumns <= 0) {
        return; // Skip layout if invalid parameters
      }

      const totalGapWidth = gapPixels * (totalColumns - 1);
      const usableWidth = width - totalGapWidth;

      // Ensure usableWidth is positive
      if (usableWidth <= 0) {
        return; // Skip layout if usableWidth is invalid
      }

      const columnWidth = usableWidth / totalColumns;

      // Ensure columnWidth is positive
      if (columnWidth <= 0) {
        return; // Skip layout if columnWidth is invalid
      }

      const unitHeight = columnWidth; // Square unit size
      const newPositions = {};

      // Sort items by position to maintain order
      const sortedItems = [...layoutItems].sort((a, b) => {
        const aPos = getWidgetViewportPosition(a, previewMode);
        const bPos = getWidgetViewportPosition(b, previewMode);
        return aPos - bPos;
      });

      // Process all items using their existing layout for the current previewMode
      sortedItems.forEach((item) => {
        const itemHeight = measuredHeights[item.id] ?? 0;
        if (itemHeight === 0) return;

        // Get columns based on preview mode
        const cols = Math.min(
          Math.max(item.layout[previewMode]?.columns || 4, 1),
          totalColumns
        );
        const itemWidth = cols * columnWidth + (cols - 1) * gapPixels;

        // Get the starting column from the layout data and ensure it's not negative
        const startCol = Math.max(
          0,
          Math.min(
            item.layout[previewMode]?.startColumn || 0,
            totalColumns - cols
          )
        );

        // Use the exact position specified in the layout
        const position = item.layout[previewMode]?.position || 0;

        // Calculate the pixel position based on the grid position
        const topPosition = position * (unitHeight + gapPixels);

        // Ensure all calculated values are valid numbers
        const leftPosition = startCol * (columnWidth + gapPixels);

        if (isNaN(leftPosition) || isNaN(topPosition) || isNaN(itemWidth)) {
          return; // Skip this item if calculations resulted in NaN
        }

        newPositions[item.id] = {
          left: leftPosition,
          top: topPosition,
          width: itemWidth,
          height: itemHeight,
          opacity: 1,
          scale: 1,
          rotate: 0,
        };
      });

      setPositions(newPositions);
      setLayoutInitialized(true);

      if (onLayoutComplete) {
        onLayoutComplete(newPositions);
      }
    },
    [
      measuredHeights,
      items,
      totalColumns,
      gapPixels,
      onLayoutComplete,
      previewMode,
    ]
  );

  const calculateLayout = useCallback(() => {
    if (!gridRef.current || !items.length) return;
    if (!mountedRef.current) return;

    const width = gridRef.current.offsetWidth;
    if (!width) return;

    if (isInitialLayout) {
      const deckPositions = {};
      items.forEach((item, i) => {
        deckPositions[item.id] = getInitialCardPosition(i, width);
      });
      setPositions(deckPositions);

      setTimeout(() => {
        if (mountedRef.current) {
          setIsInitialLayout(false);
          performMasonryLayout(width);
        }
      }, 100);
    } else {
      performMasonryLayout(width);
    }

    // Ensure we adjust the container height after layout
    requestAnimationFrame(() => {
      if (mountedRef.current) {
        updateContainerHeight();
      }
    });
  }, [isInitialLayout, items, performMasonryLayout]);

  // Helper function to update the container height based on widget positions
  const updateContainerHeight = useCallback(() => {
    if (!gridRef.current) return;

    // Add safeguards to prevent NaN values
    if (
      !containerWidth ||
      containerWidth <= 0 ||
      !totalColumns ||
      totalColumns <= 0
    ) {
      return; // Skip height calculation if invalid parameters
    }

    const totalGapWidth = gapPixels * (totalColumns - 1);
    const usableWidth = containerWidth - totalGapWidth;

    // Ensure usableWidth is positive
    if (usableWidth <= 0) {
      return; // Skip height calculation if usableWidth is invalid
    }

    const columnWidth = usableWidth / totalColumns;

    // Ensure columnWidth is positive
    if (columnWidth <= 0) {
      return; // Skip height calculation if columnWidth is invalid
    }

    // Find the bottom-most widget
    let maxBottom = 0;
    items.forEach((item) => {
      const position = item.layout[previewMode]?.position || 0;
      const height = item.layout[previewMode]?.columnHeight || 4;

      // Calculate the bottom position of this widget
      const bottom = (position + height) * (columnWidth + gapPixels);

      // Only use valid numbers
      if (!isNaN(bottom) && bottom > 0) {
        maxBottom = Math.max(maxBottom, bottom);
      }
    });

    // Set min-height on the container to accommodate all widgets
    if (maxBottom > 0) {
      gridRef.current.style.minHeight = `${maxBottom + gapPixels}px`;
    }
  }, [items, previewMode, containerWidth, totalColumns, gapPixels, gridRef]);

  // Enhanced resize handler with debounce
  const handleResize = useCallback(() => {
    if (
      isResizing ||
      isUpdatingRef.current ||
      !gridRef.current ||
      !mountedRef.current
    )
      return;

    isUpdatingRef.current = true;
    setIsResizing(true);

    try {
      // Safely get the width with null checks
      const newWidth = gridRef.current ? gridRef.current.offsetWidth || 0 : 0;

      if (newWidth !== containerWidth && newWidth > 0) {
        setContainerWidth(newWidth);
      }

      // Only proceed with measurements if we have a valid reference
      if (gridRef.current && measureRef.current) {
        measureItems();

        if (newWidth > 0) {
          performMasonryLayout(newWidth);
          updateContainerHeight();
        }
      }
    } catch (e) {
      console.warn("Error in handleResize:", e);
    } finally {
      // Always release the locks after a short delay
      setTimeout(() => {
        if (mountedRef.current) {
          setIsResizing(false);
          isUpdatingRef.current = false;
        }
      }, 100);
    }
  }, [
    containerWidth,
    measureItems,
    performMasonryLayout,
    updateContainerHeight,
  ]);

  const debouncedHandleResize = useMemo(
    () => debounce(handleResize, 100),
    [handleResize]
  );

  // Enhanced resize and orientation handling with improved cleanup
  useEffect(() => {
    if (!gridRef.current) return;
    mountedRef.current = true;

    let orientationTimeout;

    // Handle orientation changes with longer delay for viewport stabilization
    const handleOrientationChange = () => {
      if (orientationTimeout) clearTimeout(orientationTimeout);

      orientationTimeout = setTimeout(() => {
        if (mountedRef.current && gridRef.current) {
          handleResize();
        }
      }, 300); // Longer delay for orientation changes
    };

    // Handle visual viewport changes (mobile browsers with dynamic UI)
    const handleVisualViewportResize = () => {
      // Use shorter debounce for visual viewport changes
      if (mountedRef.current && gridRef.current) {
        debouncedHandleResize();
      }
    };

    handleResize(); // Initial calculation

    // Set up event listeners
    window.addEventListener("resize", debouncedHandleResize, { passive: true });
    window.addEventListener("orientationchange", handleOrientationChange, {
      passive: true,
    });

    // Listen to visual viewport changes for better mobile support
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        handleVisualViewportResize,
        { passive: true }
      );
    }

    // Cleanup
    return () => {
      mountedRef.current = false;

      if (orientationTimeout) clearTimeout(orientationTimeout);

      window.removeEventListener("resize", debouncedHandleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleVisualViewportResize
        );
      }

      debouncedHandleResize.cancel();
      setIsResizing(false);
      isUpdatingRef.current = false;
    };
  }, [debouncedHandleResize, handleResize]);

  // Force recalculation when items change (especially after publishing)
  useEffect(() => {
    if (!layoutInitialized) return;

    const recalculateTimeout = setTimeout(() => {
      if (mountedRef.current && gridRef.current) {
        measureItems();
        const width = gridRef.current.offsetWidth;
        if (width > 0) {
          performMasonryLayout(width);
          updateContainerHeight();
        }
      }
    }, 250);

    return () => clearTimeout(recalculateTimeout);
  }, [itemsSignature, layoutInitialized]);

  // Handle image loading with improved reliability
  useEffect(() => {
    if (!gridRef.current || !measureRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (
        !isResizing &&
        !isUpdatingRef.current &&
        gridRef.current &&
        mountedRef.current
      ) {
        handleResize();
      }
    });

    observer.observe(gridRef.current);

    const images = measureRef.current.getElementsByTagName("img") || [];
    const imageLoadPromises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          }
        })
    );

    Promise.all(imageLoadPromises).then(() => {
      if (!isUpdatingRef.current && gridRef.current && mountedRef.current) {
        measureItems();
        calculateLayout();
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [items, measureItems, calculateLayout, handleResize, isResizing]);

  const MeasurementDiv = useCallback(
    ({ children }) => (
      <div
        ref={measureRef}
        style={{
          width: containerWidth || "100%",
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          zIndex: -9999,
          height: 0,
        }}
      >
        {children}
      </div>
    ),
    [containerWidth]
  );

  return {
    positions,
    gridRef,
    measureRef,
    containerWidth,
    performMasonryLayout,
    measureItems,
    MeasurementDiv,
    calculateWidgetDimensions,
    updateContainerHeight,
  };
};
